/**
 * Wire-layer conversion: a request param like `startDate=2026-04-01` (a Y-M-D
 * string entered by the user via the date pickers) becomes the UTC datetime
 * for that day's start in the user's selected timezone. `endDate` becomes the
 * UTC datetime for the *last millisecond* of the day in the user's TZ, so a
 * server-side `BETWEEN start AND end` (inclusive on both ends) hits exactly
 * the calendar day(s) the user picked — no off-by-one at the day boundary.
 *
 * Anything that isn't a bare Y-M-D (`YYYY-MM-DD`) passes through untouched:
 * already-ISO datetimes, undefined params, numbers, etc.
 */

import dayjs from 'dayjs';
import { getCurrentTz } from './tzRegistry';

const Y_M_D = /^\d{4}-\d{2}-\d{2}$/;

const isYmd = (value: unknown): value is string => typeof value === 'string' && Y_M_D.test(value);

// SQL-Server-friendly datetime literal: space separator, no offset. Avoids
// two real-world pitfalls:
//   1. ISO with a 'Z' suffix is silently shifted to local server time on
//      non-UTC SQL Server hosts during implicit datetime cast.
//   2. ISO with a 'T' separator can fail implicit cast under some
//      locale/language settings.
// Space-separated `YYYY-MM-DD HH:mm:ss.SSS` is universally accepted.
const NAIVE_ISO = 'YYYY-MM-DD HH:mm:ss.SSS';

const toUtcStart = (ymd: string, tz: string): string =>
    dayjs.tz(ymd, tz).startOf('day').utc().format(NAIVE_ISO);

// Last millisecond of the day in the user's TZ, expressed as a UTC datetime.
// SQL `BETWEEN` is inclusive on both ends, so this avoids leaking the midnight
// of the next day (which would otherwise show up as "next-day 00:00" in the
// user's display when the day boundary differs from UTC's).
const toUtcEndInclusive = (ymd: string, tz: string): string =>
    dayjs.tz(ymd, tz).endOf('day').utc().format(NAIVE_ISO);

const isBareObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Returns a new params object with date-range Y-M-D strings converted to UTC
 * ISO instants. Non-Y-M-D values (and missing params) pass through untouched.
 */
export const expandDateRangeParams = (params: unknown): unknown => {
    if (!isBareObject(params)) return params;

    const tz = getCurrentTz();
    const next: Record<string, unknown> = { ...params };

    if (isYmd(next.startDate)) next.startDate = toUtcStart(next.startDate as string, tz);
    if (isYmd(next.endDate)) next.endDate = toUtcEndInclusive(next.endDate as string, tz);

    return next;
};
